import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { faPlus, faList } from '@fortawesome/free-solid-svg-icons';
import { faUser } from '@fortawesome/free-regular-svg-icons';
import CreateUserModal from './Modals/CreateUserModal';
import UserService from '../../../../Services/UserService';
import UserGroupService from '../../../../Services/UserGroupService';
import TokenService from '../../../../Services/TokenService';
import UpdateUserModal from './Modals/UpdateUserModal';
import UserTable from './UserTable/UserTable';
import XpektorButton from '../../../../Components/XpektorButton/XpektorButton';
import AddRemoveButton from '../../../../Components/AddRemoveButton/AddRemoveButton';
import ConfirmActionModal from '../../Modals/ConfirmActionModal';
import DataPanel from '../../../../Components/DataPanel/DataPanel';
import HandleAccessModal from '../../Modals/HandleAccessModal';
import CreateUserGroupModal from './Modals/CreateUserGroupModal';
import UserClientAccessService from '../../../../Services/UserClientAccessService';

/* eslint-disable react/prop-types */
export default function UserSettings({
  users,
  userGroups,
  clients,
  onUserEdit,
  onGroupEdit,
  currentUser,
}) {
  const history = useHistory();
  const [selectedUser, setSelectedUser] = useState({});

  const [deleteUserModalDescription, setDeleteUserModalDescription] = useState(
    {}
  );

  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showCreateUserModal, setShowCreateUser] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [showAddUsersToGroupModal, setShowAddUsersToGroupModal] =
    useState(false);
  const [showAddClientsToGroupModal, setShowAddClientsToGroupModal] =
    useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showUserGroupsModal, setShowUserGroupsModal] = useState(false);

  const [fetchingClientAssignments, setFetchingClientAssignments] =
    useState(false);
  const [selectedClientAssignments, setSelectedClientAssignments] = useState(
    []
  );
  const [selectedGroup, setSelectedGroup] = useState({});
  const [order, setOrder] = useState('asc');

  const sortByName = () => {
    if (order === 'asc') {
      users.sort((a, b) => {
        const nameA = a.name?.toUpperCase();
        const nameB = b.name?.toUpperCase();

        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
      setOrder('desc');
    } else if (order === 'desc') {
      users.sort((a, b) => {
        const nameA = a.name?.toUpperCase();
        const nameB = b.name?.toUpperCase();

        if (nameA < nameB) {
          return 1;
        }
        if (nameA > nameB) {
          return -1;
        }
        return 0;
      });
      setOrder('asc');
    }
  };

  async function FetchClientAssignmentsForUserGroup() {
    setFetchingClientAssignments(true);
    await UserClientAccessService.GetClientAssignmentsForUserGroup(
      selectedGroup.id
    )
      .then((response) => {
        setSelectedClientAssignments(response.data);
      })
      .catch(() => {
        setSelectedClientAssignments([]);
      })
      .finally(() => {
        setFetchingClientAssignments(false);
      });
  }

  const CheckUserToDelete = (data) => {
    if (currentUser.id === data.id) {
      setDeleteUserModalDescription(
        'OBS! Du kommer att radera ditt konto på Xpektor. Denna åtgärd kan inte ångras, du kommer att bli utloggad direkt.'
      );
    } else {
      setDeleteUserModalDescription(
        `Användare ${data.name} kommer att raderas. Denna åtgärd kan inte ångras.`
      );
    }
  };

  const onUserChange = async () => {
    await onUserEdit();
    await onGroupEdit();
  };

  const deleteUser = async () => {
    await UserService.DeleteUser(selectedUser.id)
      .then(() => {
        if (currentUser.id === selectedUser.id) {
          TokenService.removeToken();
          localStorage.clear();
          sessionStorage.clear();
          history.push('/');
        } else {
          onUserChange();
        }
      })
      .catch(() => {
        alert('Något gick fel, försök igen senare');
      });
  };

  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    if (users.length === 0) return;

    if (Object.keys(selectedGroup).length > 0) {
      setFilteredUsers(
        users.filter((user) => selectedGroup.members.includes(user.id))
      );
      return;
    }
    setFilteredUsers(users);
  }, [users, selectedGroup]);

  async function SaveUserGroupMemberChanges(changes) {
    const promises = [];

    if (!changes.users) return;

    const newSelectedGroupMembers = selectedGroup.members.filter(
      (member) => !changes.users.removedIds.includes(member)
    );

    changes.users.addedIds.forEach((userId) => {
      newSelectedGroupMembers.push(userId);
      promises.push(
        UserGroupService.addMemberToUserGroup(selectedGroup.id, userId)
      );
    });
    changes.users.removedIds.forEach((userId) => {
      promises.push(
        UserGroupService.removeMemberFromUserGroup(selectedGroup.id, userId)
      );
    });

    await Promise.all(promises).finally(() => {
      onGroupEdit();
      setSelectedGroup({ ...selectedGroup, members: newSelectedGroupMembers });
    });
  }

  async function SaveUserGroupClientAssignmentChanges(changes) {
    const promises = [];

    if (!changes.clients) return;

    changes.clients.addedIds.forEach((clientId) => {
      promises.push(
        UserClientAccessService.AssignClientToUserGroup(
          clientId,
          selectedGroup.id
        )
      );
    });

    changes.clients.removedIds.forEach((clientId) => {
      promises.push(
        UserClientAccessService.UnassignClientFromUserGroup(
          clientId,
          selectedGroup.id
        )
      );
    });

    await Promise.all(promises);
  }

  async function AssignUserOnGroups(changes) {
    const promises = [];
    if (!changes.groups) return;

    changes.groups.addedIds.forEach((groupID) => {
      promises.push(
        UserGroupService.addMemberToUserGroup(groupID, selectedUser.id)
      );
    });

    await Promise.all(promises).finally(() => {
      onGroupEdit();
    });
  }

  const deleteGroup = async () => {
    await UserGroupService.deleteUserGroup(selectedGroup.id)
      .then(() => {
        setSelectedGroup({});
        onGroupEdit();
      })
      .catch(() => {});
    return true;
  };

  return (
    <div className="settings-content">
      <div className="user-settings-content">
        <div className="content-column">
          <button
            className={`user-group-button ${
              Object.keys(selectedGroup).length === 0
                ? 'selected-group-button'
                : ''
            }`}
            type="button"
            onClick={() => {
              setSelectedGroup({});
            }}
          >
            Alla användare
          </button>
          <DataPanel
            title="Användargrupper"
            titleSize="m"
            minHeight="400px"
            maxHeight="400px"
            scroll
          >
            <div className="user-group-list">
              {userGroups.length > 0 ? (
                userGroups.map((group) => {
                  return (
                    <button
                      key={group.id}
                      className={`user-group-button ${
                        selectedGroup.id === group.id
                          ? 'selected-group-button'
                          : ''
                      }`}
                      type="button"
                      onClick={() => {
                        setSelectedGroup(group);
                      }}
                    >
                      <p>{group.name}</p> <p>{group.members.length}</p>
                    </button>
                  );
                })
              ) : (
                <p className="no-groups-entry">Finns inga användargrupper</p>
              )}
            </div>
          </DataPanel>
          <AddRemoveButton
            text="Skapa grupp"
            type="add"
            onClick={() => setShowCreateGroupModal(true)}
          />
        </div>
        <div className="content-column">
          <div className="row-container">
            {Object.keys(selectedGroup).length !== 0 ? (
              <div className="spaced-row">
                <div className="row-container">
                  <XpektorButton
                    content="Ändra medlemmar"
                    icon={faUser}
                    variant="outline"
                    onClick={() => setShowAddUsersToGroupModal(true)}
                  />
                  <XpektorButton
                    content="Tilldelade klienter"
                    icon={faList}
                    variant="outline"
                    onClick={() => {
                      FetchClientAssignmentsForUserGroup();
                      setShowAddClientsToGroupModal(true);
                    }}
                  />
                </div>
                <XpektorButton
                  content="Radera grupp"
                  variant="outline-red"
                  onClick={() => setShowDeleteGroupModal(true)}
                />
              </div>
            ) : (
              <XpektorButton
                content="Skapa användare"
                icon={faPlus}
                onClick={() => {
                  setShowCreateUser(true);
                }}
              />
            )}
          </div>
          <DataPanel
            title={
              Object.keys(selectedGroup).length > 0
                ? `Användargrupp: ${selectedGroup.name ?? 'N/A'}`
                : null
            }
            titleSize="m"
            minHeight="600px"
            maxHeight="600px"
            scroll
          >
            <UserTable sortByName={sortByName} order={order}>
              {filteredUsers.map((data) => {
                return (
                  <UserTable.Item
                    onEditClick={() => {
                      setSelectedUser(data);
                      setShowEditUserModal(true);
                    }}
                    onDeleteClick={() => {
                      setSelectedUser(data);
                      CheckUserToDelete(data);
                      setShowDeleteUserModal(true);
                    }}
                    user={data}
                    key={data.id}
                    allowDelete={Object.keys(selectedGroup).length === 0}
                  />
                );
              })}
            </UserTable>
            {filteredUsers.length === 0 ? (
              <p className="no-items-item">
                {Object.keys(selectedGroup).length === 0
                  ? 'Finns inga användare'
                  : 'Finns inga medlemmar'}
              </p>
            ) : (
              ''
            )}
          </DataPanel>
        </div>
      </div>
      {showEditUserModal && (
        <UpdateUserModal
          handleClose={() => {
            setSelectedUser({});
            setShowEditUserModal(false);
          }}
          selectedUser={selectedUser}
          onUpdated={async () => {
            await onUserEdit();
            setShowEditUserModal(false);
            setSelectedUser({});
          }}
        />
      )}
      {showCreateUserModal && (
        <CreateUserModal
          handleClose={() => setShowCreateUser(false)}
          onCreated={async (createdUser) => {
            setSelectedUser(createdUser);
            await onUserEdit();
            setShowUserGroupsModal(true);
          }}
        />
      )}
      {showDeleteUserModal && (
        <ConfirmActionModal
          handleClose={() => {
            setShowDeleteUserModal(false);
            setSelectedUser({});
          }}
          description={deleteUserModalDescription}
          actionButtonText="Ta bort"
          onConfirm={deleteUser}
          danger
        />
      )}
      {showDeleteGroupModal && (
        <ConfirmActionModal
          handleClose={() => setShowDeleteGroupModal(false)}
          description={`Du kommer att ta bort användargruppen ${selectedGroup.name}. Inga användare kommer att tas bort.`}
          actionButtonText="Ta bort"
          onConfirm={deleteGroup}
          danger
        />
      )}
      {showAddUsersToGroupModal && (
        <HandleAccessModal
          modalTitle={`Ändra medlemmar i ${selectedGroup?.name}`}
          memberPanelTitle="Medlemmar"
          handleClose={() => setShowAddUsersToGroupModal(false)}
          data={[
            {
              type: 'users',
              all: users,
              members: selectedGroup?.members ?? [],
            },
          ]}
          onSave={(changes) => SaveUserGroupMemberChanges(changes)}
        />
      )}
      {showUserGroupsModal && (
        <HandleAccessModal
          modalTitle={`Lägg till ${selectedUser.name} på användargrupper`}
          memberPanelTitle="Valda grupper"
          handleClose={() => setShowUserGroupsModal(false)}
          data={[{ type: 'groups', all: userGroups, members: [] }]}
          onSave={(changes) => AssignUserOnGroups(changes)}
        />
      )}
      {showAddClientsToGroupModal && (
        <HandleAccessModal
          modalTitle={`Klienter tilldelade på ${selectedGroup.name}`}
          memberPanelTitle="Tilldelade klienter"
          handleClose={() => {
            setShowAddClientsToGroupModal(false);
            setSelectedClientAssignments([]);
          }}
          data={[
            {
              type: 'clients',
              all: clients,
              members: selectedClientAssignments,
            },
          ]}
          onSave={(changes) => SaveUserGroupClientAssignmentChanges(changes)}
          loading={fetchingClientAssignments}
        />
      )}
      {showCreateGroupModal && (
        <CreateUserGroupModal
          handleClose={() => setShowCreateGroupModal(false)}
          onGroupCreated={() => onGroupEdit()}
        />
      )}
    </div>
  );
}
